/**
 * Tech Data Index - exports all tech data
 */

import type { Tech, TechId, TechBranch } from '../TechTypes';
import { BINGFA_TECHS } from './BingfaTechs';
import { BAIGONG_TECHS } from './BaigongTechs';
import { NONGGENG_TECHS } from './NonggengTechs';
import { SHANGDAO_TECHS } from './ShangdaoTechs';
import { MOXUE_TECHS } from './MoxueTechs';
import { YINYANG_TECHS } from './YinyangTechs';
import { FACTION_TECHS } from './FactionTechs';
import { ULTIMATE_TECH } from './UltimateTech';

// Re-export individual branch techs
export { BINGFA_TECHS } from './BingfaTechs';
export { BAIGONG_TECHS } from './BaigongTechs';
export { NONGGENG_TECHS } from './NonggengTechs';
export { SHANGDAO_TECHS } from './ShangdaoTechs';
export { MOXUE_TECHS } from './MoxueTechs';
export { YINYANG_TECHS } from './YinyangTechs';
export { FACTION_TECHS, YAN_TECHS, HAN_TECHS, LIN_TECHS, SHANG_TECHS, XUAN_TECHS, TIE_TECHS, YUN_TECHS } from './FactionTechs';
export { ULTIMATE_TECH, getUltimatePrereqDescriptions } from './UltimateTech';

// Combined list of all techs
export const ALL_TECHS: Tech[] = [
  ...BINGFA_TECHS,
  ...BAIGONG_TECHS,
  ...NONGGENG_TECHS,
  ...SHANGDAO_TECHS,
  ...MOXUE_TECHS,
  ...YINYANG_TECHS,
  ...FACTION_TECHS,
  ULTIMATE_TECH,
];

// Tech lookup map for O(1) access
const TECH_MAP: Map<TechId, Tech> = new Map(
  ALL_TECHS.map(tech => [tech.id, tech])
);

// Get tech by ID
export function getTech(id: TechId): Tech | undefined {
  return TECH_MAP.get(id);
}

// Get all techs for a specific branch
export function getTechsByBranch(branch: TechBranch): Tech[] {
  return ALL_TECHS.filter(tech => tech.branch === branch);
}

// Get all techs for a specific tier
export function getTechsByTier(tier: number): Tech[] {
  return ALL_TECHS.filter(tech => tech.tier === tier);
}

// Get tech count by branch
export function getTechCountByBranch(): Record<TechBranch, number> {
  const counts: Partial<Record<TechBranch, number>> = {};
  for (const tech of ALL_TECHS) {
    counts[tech.branch] = (counts[tech.branch] || 0) + 1;
  }
  return counts as Record<TechBranch, number>;
}

// Get total tech count
export function getTotalTechCount(): number {
  return ALL_TECHS.length;
}

// Validate tech tree integrity
export function validateTechTree(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (const tech of ALL_TECHS) {
    // Check prerequisites exist
    for (const prereqId of tech.prerequisites) {
      if (!TECH_MAP.has(prereqId)) {
        errors.push(`Tech "${tech.id}" has non-existent prerequisite "${prereqId}"`);
      }
    }

    // Check alternative prerequisites exist
    if (tech.alternativePrereqs) {
      for (const group of tech.alternativePrereqs) {
        for (const prereqId of group) {
          if (!TECH_MAP.has(prereqId)) {
            errors.push(`Tech "${tech.id}" has non-existent alternative prerequisite "${prereqId}"`);
          }
        }
      }
    }

    // Check mutual exclusions are symmetric
    if (tech.mutuallyExclusive) {
      for (const exclusiveId of tech.mutuallyExclusive) {
        const otherTech = TECH_MAP.get(exclusiveId);
        if (!otherTech) {
          errors.push(`Tech "${tech.id}" has non-existent mutual exclusion "${exclusiveId}"`);
        } else if (!otherTech.mutuallyExclusive?.includes(tech.id)) {
          errors.push(`Mutual exclusion not symmetric: "${tech.id}" excludes "${exclusiveId}" but not vice versa`);
        }
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// Get all prerequisites for a tech (recursive)
export function getAllPrerequisites(techId: TechId): TechId[] {
  const tech = getTech(techId);
  if (!tech) return [];

  const allPrereqs = new Set<TechId>();
  const stack = [...tech.prerequisites];

  while (stack.length > 0) {
    const prereqId = stack.pop()!;
    if (allPrereqs.has(prereqId)) continue;

    allPrereqs.add(prereqId);
    const prereqTech = getTech(prereqId);
    if (prereqTech) {
      stack.push(...prereqTech.prerequisites);
    }
  }

  return Array.from(allPrereqs);
}

// Get all techs that depend on a specific tech
export function getDependentTechs(techId: TechId): TechId[] {
  return ALL_TECHS
    .filter(tech =>
      tech.prerequisites.includes(techId) ||
      tech.alternativePrereqs?.some(group => group.includes(techId))
    )
    .map(tech => tech.id);
}
